/**
 * 项目元素（角色、场景、道具）API Hooks
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { projectElementsApi } from '@/services/api/project-elements';
import type {
  CharacterDetailResponse,
  LocationDetailResponse,
  PropDetailResponse,
  ProjectCharacterListResponse,
  ProjectCharacterUpdatePayload,
  ProjectLocationListResponse,
  ProjectLocationUpdatePayload,
  ProjectPropListResponse,
  ProjectPropUpdatePayload,
  ProjectCharacterResponse,
  ProjectLocationResponse,
  ProjectPropResponse,
} from '@/services/api/project-elements';
import { mergeElementMetaData } from '@/lib/custom-fields';
import { elementDetailKeys } from './useProjectElementsDetail';
import type { PaginatedResourceList, ResourceLibraryType } from './useResourceLibrary';
import type { Resource } from '@/types';

type ProjectElementUpdateType = 'character' | 'location' | 'prop';
type ProjectElementUpdateResponse =
  | ProjectCharacterResponse
  | ProjectLocationResponse
  | ProjectPropResponse;
type ProjectElementListItem =
  | ProjectCharacterListResponse
  | ProjectLocationListResponse
  | ProjectPropListResponse;

function getUpdatedElementName(data: ProjectElementUpdateResponse): string {
  return data.name;
}

function getUpdatedElementDescription(data: ProjectElementUpdateResponse): string | null {
  return data.description ?? null;
}

function getUpdatedElementImageUrl(data: ProjectElementUpdateResponse): string | null | undefined {
  if ('characterImageUrl' in data) return data.characterImageUrl ?? data.defaultThumbnailUrl;
  if ('locationImageUrl' in data) return data.locationImageUrl ?? data.defaultThumbnailUrl;
  if ('propImageUrl' in data) return data.propImageUrl ?? data.defaultThumbnailUrl;
  return data.defaultThumbnailUrl;
}

function updateResourceLibraryItem(
  resource: Resource,
  data: ProjectElementUpdateResponse
): Resource {
  return {
    ...resource,
    name: getUpdatedElementName(data),
    description: getUpdatedElementDescription(data) ?? undefined,
    thumbnailUrl: getUpdatedElementImageUrl(data) ?? resource.thumbnailUrl,
    fileUrl: getUpdatedElementImageUrl(data) ?? resource.fileUrl,
    metadata: {
      ...resource.metadata,
      description: getUpdatedElementDescription(data) ?? '',
    },
    updatedAt: data.updatedAt || resource.updatedAt,
  };
}

function updateResourceLibraryItems(
  items: Resource[] | undefined,
  elementId: string,
  data: ProjectElementUpdateResponse
): Resource[] | undefined {
  return items?.map((item) =>
    item.id === elementId ? updateResourceLibraryItem(item, data) : item
  );
}

function isResourceLibraryProjectTypeKey(
  key: readonly unknown[],
  projectId: string,
  type: ResourceLibraryType
): boolean {
  return (
    key[0] === 'resource-library' &&
    key[1] === 'v1' &&
    (key[2] === 'list' || key[2] === 'infinite') &&
    key[3] === projectId &&
    key[4] === type
  );
}

function markResourceLibraryQueriesStale(
  queryClient: QueryClient,
  projectId: string,
  type: ResourceLibraryType
): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && isResourceLibraryProjectTypeKey(key, projectId, type);
    },
    refetchType: 'none',
  });
}

function patchResourceLibraryCaches(
  queryClient: QueryClient,
  projectId: string,
  type: ResourceLibraryType,
  elementId: string,
  data: ProjectElementUpdateResponse
): void {
  queryClient.setQueriesData<Resource[]>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === 'resource-library' &&
          key[1] === 'v1' &&
          key[2] === 'list' &&
          key[3] === projectId &&
          key[4] === type
        );
      },
    },
    (current) => updateResourceLibraryItems(current, elementId, data)
  );

  queryClient.setQueriesData<InfiniteData<PaginatedResourceList>>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === 'resource-library' &&
          key[1] === 'v1' &&
          key[2] === 'infinite' &&
          key[3] === projectId &&
          key[4] === type
        );
      },
    },
    (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              items: updateResourceLibraryItems(page.items, elementId, data) ?? page.items,
            })),
          }
        : current
  );
}

function markProjectElementListStale(
  queryClient: QueryClient,
  projectId: string,
  type: ProjectElementUpdateType
): void {
  const queryKey =
    type === 'character'
      ? projectElementKeys.characters(projectId)
      : type === 'location'
        ? projectElementKeys.locations(projectId)
        : projectElementKeys.props(projectId);

  void queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
}

function patchProjectElementListCaches(
  queryClient: QueryClient,
  projectId: string,
  type: ProjectElementUpdateType,
  elementId: string,
  data: ProjectElementUpdateResponse
): void {
  const queryKey =
    type === 'character'
      ? projectElementKeys.characters(projectId)
      : type === 'location'
        ? projectElementKeys.locations(projectId)
        : projectElementKeys.props(projectId);

  queryClient.setQueriesData<ProjectElementListItem[]>({ queryKey }, (current) =>
    current?.map((item) => {
      const currentId =
        'characterId' in item
          ? item.characterId
          : 'locationId' in item
            ? item.locationId
            : item.propId;
      if (currentId !== elementId) return item;
      return {
        ...item,
        name: getUpdatedElementName(data),
        description: getUpdatedElementDescription(data),
        coverUrl: getUpdatedElementImageUrl(data) ?? item.coverUrl,
        ...('isOffscreen' in item && 'isOffscreen' in data
          ? { isOffscreen: data.isOffscreen }
          : {}),
      };
    })
  );
}

function patchElementDetailCache(
  queryClient: QueryClient,
  projectId: string,
  type: ProjectElementUpdateType,
  elementId: string,
  data: ProjectElementUpdateResponse
): void {
  if (type === 'character') {
    queryClient.setQueryData<CharacterDetailResponse>(
      elementDetailKeys.character(projectId, elementId),
      (current) =>
        current
          ? {
              ...current,
              name: data.name,
              description: data.description ?? null,
              characterImageUrl:
                'characterImageUrl' in data
                  ? (data.characterImageUrl ?? current.characterImageUrl)
                  : current.characterImageUrl,
              roleType: 'roleType' in data ? String(data.roleType) : current.roleType,
              isOffscreen: 'isOffscreen' in data ? data.isOffscreen : current.isOffscreen,
              metaData: mergeElementMetaData(current.metaData, data.metaData),
            }
          : current
    );
    return;
  }

  if (type === 'location') {
    queryClient.setQueryData<LocationDetailResponse>(
      elementDetailKeys.location(projectId, elementId),
      (current) =>
        current
          ? {
              ...current,
              name: data.name,
              description: data.description ?? null,
              locationImageUrl:
                'locationImageUrl' in data
                  ? (data.locationImageUrl ?? current.locationImageUrl)
                  : current.locationImageUrl,
              location: 'location' in data ? (data.location ?? null) : current.location,
              metaData: mergeElementMetaData(current.metaData, data.metaData),
            }
          : current
    );
    return;
  }

  queryClient.setQueryData<PropDetailResponse>(
    elementDetailKeys.prop(projectId, elementId),
    (current) =>
      current
        ? {
            ...current,
            name: data.name,
            description: data.description ?? null,
            propImageUrl:
              'propImageUrl' in data
                ? (data.propImageUrl ?? current.propImageUrl)
                : current.propImageUrl,
            metaData: mergeElementMetaData(current.metaData, data.metaData),
          }
        : current
  );
}

export function syncProjectElementUpdateCaches(
  queryClient: QueryClient,
  params: {
    projectId: string;
    type: ProjectElementUpdateType;
    elementId: string;
    data: ProjectElementUpdateResponse;
  }
): void {
  patchElementDetailCache(
    queryClient,
    params.projectId,
    params.type,
    params.elementId,
    params.data
  );
  patchProjectElementListCaches(
    queryClient,
    params.projectId,
    params.type,
    params.elementId,
    params.data
  );
  patchResourceLibraryCaches(
    queryClient,
    params.projectId,
    params.type,
    params.elementId,
    params.data
  );
  markProjectElementListStale(queryClient, params.projectId, params.type);
  markResourceLibraryQueriesStale(queryClient, params.projectId, params.type);
}

/**
 * Query Keys
 */
export const projectElementKeys = {
  all: ['project-elements'] as const,
  characters: (parentProjectId: string) =>
    [...projectElementKeys.all, 'characters', parentProjectId] as const,
  locations: (parentProjectId: string) =>
    [...projectElementKeys.all, 'locations', parentProjectId] as const,
  props: (parentProjectId: string) =>
    [...projectElementKeys.all, 'props', parentProjectId] as const,
};

/**
 * 获取项目角色列表（分页，支持搜索）
 */
export function useProjectCharacters(
  parentProjectId: string | null,
  options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    includeTags?: boolean;
    enabled?: boolean;
  }
) {
  return useQuery({
    queryKey: [
      ...projectElementKeys.characters(parentProjectId!),
      options?.page,
      options?.pageSize,
      options?.search,
      options?.includeTags,
    ],
    queryFn: async () => {
      const res = await projectElementsApi.getCharacters(parentProjectId!, {
        page: options?.page,
        pageSize: options?.pageSize,
        search: options?.search,
        includeTags: options?.includeTags,
      });
      return res.items;
    },
    enabled: !!parentProjectId && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
  });
}

/**
 * 获取项目场景列表（分页，支持搜索）
 */
export function useProjectLocations(
  parentProjectId: string | null,
  options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    includeTags?: boolean;
    enabled?: boolean;
  }
) {
  return useQuery({
    queryKey: [
      ...projectElementKeys.locations(parentProjectId!),
      options?.page,
      options?.pageSize,
      options?.search,
      options?.includeTags,
    ],
    queryFn: async () => {
      const res = await projectElementsApi.getLocations(parentProjectId!, {
        page: options?.page,
        pageSize: options?.pageSize,
        search: options?.search,
        includeTags: options?.includeTags,
      });
      return res.items;
    },
    enabled: !!parentProjectId && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
  });
}

/**
 * 获取项目道具列表（分页，支持搜索）
 */
export function useProjectProps(
  parentProjectId: string | null,
  options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    includeTags?: boolean;
    enabled?: boolean;
  }
) {
  return useQuery({
    queryKey: [
      ...projectElementKeys.props(parentProjectId!),
      options?.page,
      options?.pageSize,
      options?.search,
      options?.includeTags,
    ],
    queryFn: async () => {
      const res = await projectElementsApi.getProps(parentProjectId!, {
        page: options?.page,
        pageSize: options?.pageSize,
        search: options?.search,
        includeTags: options?.includeTags,
      });
      return res.items;
    },
    enabled: !!parentProjectId && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
  });
}

/**
 * 更新角色
 */
export function useUpdateCharacter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentProjectId,
      characterId,
      data,
    }: {
      parentProjectId: string;
      characterId: string;
      data: ProjectCharacterUpdatePayload;
    }) => projectElementsApi.updateCharacter(parentProjectId, characterId, data),
    onSuccess: (data: ProjectCharacterResponse, variables) => {
      syncProjectElementUpdateCaches(queryClient, {
        projectId: variables.parentProjectId,
        type: 'character',
        elementId: variables.characterId,
        data,
      });
    },
  });
}

/**
 * 更新场景
 */
export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentProjectId,
      locationId,
      data,
    }: {
      parentProjectId: string;
      locationId: string;
      data: ProjectLocationUpdatePayload;
    }) => projectElementsApi.updateLocation(parentProjectId, locationId, data),
    onSuccess: (data: ProjectLocationResponse, variables) => {
      syncProjectElementUpdateCaches(queryClient, {
        projectId: variables.parentProjectId,
        type: 'location',
        elementId: variables.locationId,
        data,
      });
    },
  });
}

/**
 * 更新道具
 */
export function useUpdateProp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentProjectId,
      propId,
      data,
    }: {
      parentProjectId: string;
      propId: string;
      data: ProjectPropUpdatePayload;
    }) => projectElementsApi.updateProp(parentProjectId, propId, data),
    onSuccess: (data: ProjectPropResponse, variables) => {
      syncProjectElementUpdateCaches(queryClient, {
        projectId: variables.parentProjectId,
        type: 'prop',
        elementId: variables.propId,
        data,
      });
    },
  });
}

/**
 * 失活项目元素列表缓存。
 * 在创建/删除/更新角色、场景、道具，或修改其标签/装扮后调用。
 * type 为可选，不传则失活所有类型。
 */
export function invalidateProjectElementsList(
  queryClient: QueryClient,
  projectId: string,
  type?: 'character' | 'location' | 'prop'
): void {
  if (!type) {
    void queryClient.invalidateQueries({ queryKey: projectElementKeys.all });
    return;
  }
  const queryKey =
    type === 'character'
      ? projectElementKeys.characters(projectId)
      : type === 'location'
        ? projectElementKeys.locations(projectId)
        : projectElementKeys.props(projectId);
  void queryClient.invalidateQueries({ queryKey });
}

// ─── 缓存读取辅助函数（供其他 hook 调用，勿直接 import keys）────────────────

/**
 * 从 QueryClient 缓存读取指定项目的角色列表数据（不触发请求）。
 */
export function getProjectCharactersData(queryClient: QueryClient, projectId: string) {
  return queryClient.getQueriesData<ProjectCharacterResponse[]>({
    queryKey: projectElementKeys.characters(projectId),
  });
}

/**
 * 从 QueryClient 缓存读取指定项目的场景列表数据（不触发请求）。
 */
export function getProjectLocationsData(queryClient: QueryClient, projectId: string) {
  return queryClient.getQueriesData<ProjectLocationResponse[]>({
    queryKey: projectElementKeys.locations(projectId),
  });
}

/**
 * 从 QueryClient 缓存读取指定项目的道具列表数据（不触发请求）。
 */
export function getProjectPropsData(queryClient: QueryClient, projectId: string) {
  return queryClient.getQueriesData<ProjectPropResponse[]>({
    queryKey: projectElementKeys.props(projectId),
  });
}

export function invalidateAllProjectElements(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: projectElementKeys.all });
}

export function invalidateProjectCharacters(queryClient: QueryClient, projectId: string): void {
  void queryClient.invalidateQueries({ queryKey: projectElementKeys.characters(projectId) });
}

export function invalidateProjectLocations(queryClient: QueryClient, projectId: string): void {
  void queryClient.invalidateQueries({ queryKey: projectElementKeys.locations(projectId) });
}

export function invalidateProjectProps(queryClient: QueryClient, projectId: string): void {
  void queryClient.invalidateQueries({ queryKey: projectElementKeys.props(projectId) });
}

/**
 * 获取项目元素的筛选选项（角色、场景、道具等的元数据聚合）。
 * @param projectId - 父级项目 ID
 * @param elementType - 元素类型（'character' | 'location' | 'prop'），传 'footage' 或不传时禁用请求
 */
export function useProjectElementFilterOptions(projectId: string | null, elementType?: string) {
  return useQuery({
    queryKey: ['projectElements', 'filterOptions', projectId!, elementType],
    queryFn: () => projectElementsApi.getFilterOptions(projectId!, elementType),
    enabled: !!projectId && !!elementType && elementType !== 'footage',
    staleTime: 1000 * 60 * 5,
  });
}
